"use client";

import React from "react";
import { Mail, ArrowRight, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 text-primary">
                    <CircleCheck className="w-8 h-8" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-2xl font-bold tracking-tight text-foreground">Message Received</h3>
                    <p className="text-muted-foreground max-w-sm mx-auto">
                        {successMessage}
                    </p>
                </div>
                <Button onClick={resetForm} variant="outline" className="mt-4">
                    Send Another Message
                </Button>
            </div>;
  }
  const inputBaseClasses = "flex w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField control={control} name="name" rules={{
        required: "Your Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="sm:col-span-2">
                            <FormLabel>Your Name</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="John Doe" className={`${inputBaseClasses} h-10`} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                                <input type="email" placeholder="john@example.com" className={`${inputBaseClasses} h-10`} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                                <input type="tel" placeholder="+1 (555) 123-4567" className={`${inputBaseClasses} h-10`} {...field} />
                            </FormControl>
                            <FormDescription>Optional, but helps us respond faster</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                            <textarea placeholder="How can we help you?" className={`${inputBaseClasses} min-h-[120px] resize-y`} {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
                    <CircleAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive-foreground">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto group bg-[var(--gradient-primary)] hover:brightness-110 transition-all border-none">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 w-4 h-4" />
                        Sending...
                    </> : <>
                        Send Message
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </>}
            </Button>
        </Form>;
}
export default function GetInTouch() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-in-touch" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-primary/5 blur-[120px] rounded-full" />
                <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 blur-[100px] rounded-full" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-border/20 to-transparent" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-[1px] bg-gradient-to-b from-transparent via-border/20 to-transparent" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
                    
                    {}
                    <div ref={leftRef} className={`space-y-16 transition-all duration-1000 ease-out ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div>
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6">
                                Let's build<br />
                                <span className="text-primary">something extraordinary.</span>
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-md">
                                Whether you're looking to scale your branded campaigns or need high-quality aged leads, our team is ready to accelerate your growth.
                            </p>
                        </div>

                        <div className="space-y-12">
                            <div className="group">
                                <h3 className="flex items-center gap-2 text-sm font-mono text-muted-foreground uppercase tracking-widest mb-4">
                                    <Mail className="w-4 h-4" />
                                    General Inquiries
                                </h3>
                                <Link to="mailto:info@axeboxmedia.com" className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground hover:text-primary transition-colors inline-block">
                                    info@axeboxmedia.com
                                </Link>
                            </div>
                            
                            <Separator className="bg-border/40 w-3/4" />
                            
                            <div className="group">
                                <h3 className="flex items-center gap-2 text-sm font-mono text-muted-foreground uppercase tracking-widest mb-4">
                                    <Mail className="w-4 h-4" />
                                    Sales & Lead Generation
                                </h3>
                                <Link to="mailto:success@axeboxmedia.com" className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground hover:text-primary transition-colors inline-block">
                                    success@axeboxmedia.com
                                </Link>
                            </div>
                        </div>
                    </div>

                    {}
                    <div ref={rightRef} className={`transition-all duration-1000 delay-200 ease-out ${rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <Card className="bg-card/40 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-[2px] bg-[var(--gradient-primary)]" />
                            <CardHeader className="pb-8 pt-10">
                                <CardTitle className="text-2xl">Get In Touch</CardTitle>
                                <CardDescription className="text-base">
                                    Fill out the form below and we'll get back to you as soon as possible.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <FormProvider formId="contact_form_connect_with_us" sectionName="get-in-touch" defaultValues={{
                name: "",
                email: "",
                phone: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Message" successMessage="Thank you for reaching out. Our team will review your message and contact you shortly." errorMessage="We encountered an issue sending your message. Please try again or use the email addresses provided.">
                                    <ContactForm />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}